import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { hoSoToChucRepository } from '../../../lib/repositories/ho-so-to-chuc.repository';
import { useToast } from '../../../lib/toast';
import { usePhamVi } from '../../../hooks/usePhamVi';
import { thuLai } from '../../vai-tro/hooks/useVaiTro';
import { MOT_TRANG_LICH_SU } from '../constants';
import type {
  LogoSlot,
  TenantProfile,
  TenantProfileUpdate,
  TenantProfileVersionPage,
} from '../types';

export const KHOA_HO_SO = {
  hoSo: ['ho-so-to-chuc', 'ho-so'] as const,
  lichSu: ['ho-so-to-chuc', 'lich-su'] as const,
};

/**
 * Hồ sơ nhận diện hiện hành. Chỉ tải ở cổng tenant: nhân sự nền tảng không
 * thuộc tổ chức nào nên không có hồ sơ để đọc (ADR-UI-0012).
 */
export function useHoSoToChuc() {
  const congTenant = usePhamVi() === 'tenant';
  return useQuery<TenantProfile, ApiError>({
    queryKey: KHOA_HO_SO.hoSo,
    queryFn: () => hoSoToChucRepository.doc(),
    enabled: congTenant,
    staleTime: 60_000,
    retry: thuLai,
  });
}

/** Lịch sử phiên bản; chỉ tải khi hộp đang mở. */
export function useLichSuHoSo(mo: boolean) {
  return useQuery<TenantProfileVersionPage, ApiError>({
    queryKey: KHOA_HO_SO.lichSu,
    queryFn: () => hoSoToChucRepository.lichSu({ limit: MOT_TRANG_LICH_SU }),
    enabled: mo,
    staleTime: 0,
    retry: thuLai,
  });
}

/**
 * Chung cho mọi thao tác ghi: lấy phiên bản hiện hành làm `If-Match`, ghi kết
 * quả vào cache, làm mới lịch sử. `412` là người khác vừa sửa: tải lại hồ sơ để
 * form hiện bản mới nhất, và để người dùng quyết có lưu lại hay không.
 */
function useGhiHoSo<V>(
  ghi: (v: V, phienBan: number) => Promise<TenantProfile>,
  thongBao: (v: V, t: ReturnType<typeof useT>['t']) => string,
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<TenantProfile, ApiError, { v: V; phienBan: number }>({
    mutationFn: ({ v, phienBan }) => ghi(v, phienBan),
    onSuccess: (hoSo, { v }) => {
      queryClient.setQueryData(KHOA_HO_SO.hoSo, hoSo);
      void queryClient.invalidateQueries({ queryKey: KHOA_HO_SO.lichSu });
      toast.success(thongBao(v, t));
    },
    onError: (err) => {
      if (err.status === 412) void queryClient.invalidateQueries({ queryKey: KHOA_HO_SO.hoSo });
    },
  });
}

export function useCapNhatHoSo() {
  return useGhiHoSo<TenantProfileUpdate>(
    (du, v) => hoSoToChucRepository.capNhat(du, v),
    (_du, t) => t('hoSoToChuc.daLuu'),
  );
}

export function useThayLogo() {
  return useGhiHoSo<{ slot: LogoSlot; tep: File }>(
    ({ slot, tep }, v) => hoSoToChucRepository.thayLogo(slot, tep, v),
    (_v, t) => t('hoSoToChuc.daDoiAnh'),
  );
}

export function useGoLogo() {
  return useGhiHoSo<LogoSlot>(
    (slot, v) => hoSoToChucRepository.goLogo(slot, v),
    (_slot, t) => t('hoSoToChuc.daGoAnh'),
  );
}

export function useKhoiPhucHoSo() {
  return useGhiHoSo<number>(
    (phienBan, v) => hoSoToChucRepository.khoiPhuc(phienBan, v),
    (phienBan, t) => t('hoSoToChuc.lichSu.daKhoiPhuc', { so: phienBan }),
  );
}
